import {useMemo} from 'react';

import {findLastIndex} from '../../common/util';
import {useAppSelector} from '../../store';
import {GameLogicError} from '../errors';
import {computeQuestion, harvestSeasonLength} from '../mechanics';
import {getEvent} from '../resources/events';
import {useJeopardyWheels} from '../resources/jeopardies';
import {QUESTIONS} from '../resources/questions';
import {useAllTools} from '../resources/tools';
import {ChoiceLog, Event, EventLog, GamePhase, JeopardyWheel} from '../types';

export const useGameStatus = () => useAppSelector(state => state.game);

export function useCurrentQuestionComputed() {
  const gameStatus = useGameStatus();

  // Current question matches the last element of current year's choices
  const choices: ChoiceLog[] | undefined = gameStatus.history[gameStatus.currentYear]?.choices;
  if (!choices) return undefined;
  const currentId = choices[choices.length - 1]?.question;
  if (!currentId) return undefined;

  const current = QUESTIONS[currentId];
  try {
    // Do not pass "current" directly to avoid object mutation
    return computeQuestion(gameStatus, {...current});
  } catch (err) {
    console.error(err);
    return undefined;
  }
}

export const useEnabledTools = () => {
  const allTools = useAllTools();
  const enabledIds = useAppSelector(state => state.game.enabledTools);
  const result = useMemo(() => allTools.filter(t => enabledIds.includes(t.id)), [allTools, enabledIds]);
  return result;
};

export const useMyTools = () => useAppSelector(state => state.game.tools);

/**
 * Get the event that should be currently shown on screen.
 *
 * The returned object contains the following properties. If we are not at Events phase, or there is no event to show,
 * all these properties will be undefined.
 * - currentEvent: The event to show on screen (this is the last event that was triggered --its effect was executed-- excluding silent events)
 * - currentLog: The log entry in current year's history matching the current event. It will have a non-undefined`effect`
 * - currentIndex: The index of current event (and current log) within events occurred this year
 */
export const useCurrentEvent = () => {
  const gameStatus = useGameStatus();

  // Get Event logs current year
  const eventLogs = gameStatus.history[gameStatus.currentYear]?.events;
  const events = useMemo(() => eventLogs?.map(log => GameLogicError.assertNonNullish(getEvent(log.id))), [eventLogs]);

  // Find last triggered (with effect) event (excluding silent events)
  const currentIndex = useMemo(
    () =>
      eventLogs
        ? findLastIndex(eventLogs, (log, idx) => {
            if (!log.effect) return false; // Skip non-triggered effects
            if (gameStatus.phase === GamePhase.events) return !events[idx]?.silent; // In Events phase, only a non-silent event can be selected
            if (gameStatus.phase === GamePhase.jeopardies) return !!events[idx]?.silent; // In Jeopardies phase, only a silent event can be selected
            return false; // In other phases, no event can be selected
          })
        : -1,
    [eventLogs, events, gameStatus.phase]
  );

  if (currentIndex === -1) {
    return {
      currentLog: undefined,
      currentEvent: undefined,
      currentIndex: undefined,
    };
  }

  const currentLog: EventLog = eventLogs[currentIndex];
  const currentEvent: Event = events[currentIndex];

  return {
    currentLog,
    currentEvent,
    currentIndex,
  };
};

/**
 * Get the current jeopardy wheel, if any. If we are in Jeopardies phase, the current wheel is the first wheel that
 * can be shown this year and none of its events have been added to history yet.
 */
export const useCurrentJeopardyWheel = () => {
  const gameStatus = useGameStatus();
  const jeopardyWheels = useJeopardyWheels(gameStatus);
  const yearEvents = gameStatus.history[gameStatus.currentYear]?.events;

  return useMemo<JeopardyWheel | undefined>(() => {
    if (gameStatus.phase !== GamePhase.jeopardies) return undefined;

    for (let index = 0; index < jeopardyWheels.length; index++) {
      const currentJeopardyWheel = jeopardyWheels[index];
      // If no events from this wheel have already been triggered this year, return this wheel
      if (!yearEvents?.some(eventLog => currentJeopardyWheel.eventsIds.includes(eventLog.id))) {
        return currentJeopardyWheel;
      }
    }
    return undefined;
  }, [gameStatus.phase, jeopardyWheels, yearEvents]);
};

/**
 * Gets an index representing the current growing year within current harvest season. Growing year is incremented when
 * we reach Jeopardies phase (after all questions are asked).
 *
 * Returned value can be negative if we haven't reached the first growing year, e.g. we haven't planted yet, or we haven't
 * reached first Jeopardies phase. It can also be higher that harvest period, e.g. when flood prevented harvesting.
 */
export const useGrowAnimationYear = () => {
  const gameStatus = useGameStatus();
  return useMemo(() => {
    let cropYear =
      harvestSeasonLength({
        crop: gameStatus.crop,
        currentYear: gameStatus.currentYear,
        plantingYear: gameStatus.plantingYear,
        harvestYears: gameStatus.harvestYears,
      }) - 1;
    if (gameStatus.phase < GamePhase.jeopardies) {
      // We are still in previous year if we haven't reached Jeopardies phase
      cropYear--;
    }
    return cropYear;
  }, [gameStatus.crop, gameStatus.currentYear, gameStatus.harvestYears, gameStatus.phase, gameStatus.plantingYear]);
};
